/**
 * Oil & Gas — Regulatory Reporting
 *
 * Compiles SHA-256 sealed closure records into a structured evidence pack
 * suitable for submission to DPR/NUPRC, EPA, or internal compliance teams.
 *
 * Provides:
 *   • Filterable closed-incident table with full chain-of-custody metadata
 *   • Per-incident evidence summary (detection → OGI survey → review → closure)
 *   • Print-ready regulatory summary for operator sign-off
 *   • CSV export of the filtered record set
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import {
  FileText, Lock, CheckCircle2, AlertTriangle,
  RefreshCw, Download, Printer, Shield,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClosedMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    vertical?: string;
    corridor_name?: string;
    closure_outcome?: string;
    closure_loss_volume_bbl?: number;
    closure_repair_method?: string;
    closure_regulatory?: boolean;
    closure_notes?: string;
    closure_sha256?: string;
    closure_sealed_at?: string;
    plume_rate_kg_hr?: number;
    thermal_anomaly_c?: number;
    diagnosis?: string;
    severity?: string;
    step7_status?: string;
    step7_note?: string;
    step7_reviewed_at?: string;
    step8_response_type?: string;
    step8_team_lead?: string;
    step8_erp_system?: string;
    step8_work_order?: string;
    step8_dispatched_at?: string;
    [key: string]: any;
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const OUTCOME_LABELS: Record<string, string> = {
  repaired:    'Repaired — Pipeline Returned to Service',
  shut_in:     'Shut-in — Section Isolated Pending Repair',
  false_alarm: 'False Alarm — No Physical Defect Found',
  monitored:   'Monitoring Only — No Immediate Action Required',
};

const RESPONSE_LABELS: Record<string, string> = {
  ground_inspection: 'Ground Inspection',
  pressure_test:     'Pressure Test',
  shut_in:           'Shut-in Procedure',
  emergency_repair:  'Emergency Repair',
  leak_containment:  'Leak Containment',
};

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCsv(missions: ClosedMission[], volLabel: string, massRateLabel: string) {
  const headers = [
    'Incident Name', 'Corridor', 'Outcome', `Loss (${volLabel})`,
    `Plume Rate (${massRateLabel})`, 'Repair Method', 'Regulatory Notified',
    'Response Type', 'Team Lead', 'ERP Work Order',
    'Dispatched', 'Sealed At', 'SHA-256 Hash',
  ];
  const rows = missions.map((m) => [
    m.name,
    m.metadata?.corridor_name ?? '',
    OUTCOME_LABELS[m.metadata?.closure_outcome ?? ''] ?? (m.metadata?.closure_outcome ?? ''),
    m.metadata?.closure_loss_volume_bbl ?? '',
    m.metadata?.plume_rate_kg_hr ?? '',
    m.metadata?.closure_repair_method ?? '',
    m.metadata?.closure_regulatory ? 'Yes' : 'No',
    RESPONSE_LABELS[m.metadata?.step8_response_type ?? ''] ?? (m.metadata?.step8_response_type ?? ''),
    m.metadata?.step8_team_lead ?? '',
    m.metadata?.step8_work_order
      ? `${m.metadata.step8_erp_system ?? ''} ${m.metadata.step8_work_order}`
      : '',
    m.metadata?.step8_dispatched_at
      ? new Date(m.metadata.step8_dispatched_at).toISOString()
      : '',
    m.metadata?.closure_sealed_at
      ? new Date(m.metadata.closure_sealed_at).toISOString()
      : '',
    m.metadata?.closure_sha256 ?? '',
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `nabhos_og_regulatory_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const RegulatoryReportingPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [missions, setMissions]       = useState<ClosedMission[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [filterOutcome, setFilterOutcome]       = useState('');
  const [filterRegulatory, setFilterRegulatory] = useState('');
  const [filterCorridor, setFilterCorridor]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 200 } });
      const all: ClosedMission[] = res.data?.missions ?? res.data?.data?.missions ?? [];
      setMissions(
        all
          .filter((m) => m.metadata?.vertical === 'oil_gas' && m.metadata?.closure_outcome)
          .sort((a, b) => {
            const ta = a.metadata?.closure_sealed_at ?? a.updated_at;
            const tb = b.metadata?.closure_sealed_at ?? b.updated_at;
            return new Date(tb).getTime() - new Date(ta).getTime();
          }),
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load closure records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const corridors = Array.from(new Set(missions.map((m) => m.metadata?.corridor_name).filter(Boolean))) as string[];

  const filtered = missions.filter((m) => {
    if (filterOutcome    && m.metadata?.closure_outcome !== filterOutcome) return false;
    if (filterCorridor   && m.metadata?.corridor_name   !== filterCorridor) return false;
    if (filterRegulatory === 'yes' && !m.metadata?.closure_regulatory)   return false;
    if (filterRegulatory === 'no'  &&  m.metadata?.closure_regulatory)   return false;
    return true;
  });

  const regulatoryCount = filtered.filter((m) => m.metadata?.closure_regulatory).length;
  const totalLoss       = filtered.reduce((s, m) => s + (m.metadata?.closure_loss_volume_bbl ?? 0), 0);

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-orange-500" /> Regulatory Reporting
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            SHA-256 sealed incident evidence packs for DPR / NUPRC / EPA submissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button onClick={() => exportCsv(filtered, uom.volume_unit_label, uom.mass_rate_unit_label)} disabled={filtered.length === 0}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Filters ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterCorridor} onChange={(e) => setFilterCorridor(e.target.value)} className={SELECT_CLS}>
            <option value="">All Corridors</option>
            {corridors.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterOutcome} onChange={(e) => setFilterOutcome(e.target.value)} className={SELECT_CLS}>
            <option value="">All Outcomes</option>
            {Object.entries(OUTCOME_LABELS).map(([v, l]) => <option key={v} value={v}>{l.split(' — ')[0]}</option>)}
          </select>
          <select value={filterRegulatory} onChange={(e) => setFilterRegulatory(e.target.value)} className={SELECT_CLS}>
            <option value="">All (regulatory + non)</option>
            <option value="yes">Regulatory notified only</option>
            <option value="no">Not notified</option>
          </select>
          {(filterCorridor || filterOutcome || filterRegulatory) && (
            <button
              onClick={() => { setFilterCorridor(''); setFilterOutcome(''); setFilterRegulatory(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* ── Summary banner ── */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Incidents in view',       value: filtered.length,       colour: 'text-gray-900 dark:text-white' },
            { label: 'Regulatory notified',      value: regulatoryCount,       colour: 'text-blue-700 dark:text-blue-400' },
            { label: `Total product loss (${uom.volume_unit_label})`, value: totalLoss, colour: totalLoss > 0 ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white' },
            { label: 'SHA-256 sealed',           value: filtered.filter((m) => m.metadata?.closure_sha256).length, colour: 'text-green-700 dark:text-green-400' },
          ].map(({ label, value, colour }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Evidence records ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Shield className="w-4 h-4 text-orange-500" />
            Sealed Incident Evidence Records ({filtered.length})
          </p>
          <p className="text-xs text-gray-400">Expand any row for full chain-of-custody</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {missions.length === 0 ? 'No closed incidents yet' : 'No records match the current filters'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {missions.length === 0
                ? 'Close incidents in Incident Closure (Step 8b) to generate records'
                : 'Try different filter values'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => {
              const isExpanded = expandedId === m.id;
              return (
                <div key={m.id}>
                  {/* Summary row */}
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    {m.metadata?.closure_regulatory
                      ? <Shield className="w-4 h-4 flex-shrink-0 text-blue-500" />
                      : <FileText className="w-4 h-4 flex-shrink-0 text-gray-400" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.corridor_name ?? '—'}
                        {m.metadata?.closure_sealed_at
                          ? ` · Sealed ${new Date(m.metadata.closure_sealed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {m.metadata?.closure_regulatory && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
                          Notified
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        m.metadata?.closure_outcome === 'repaired'    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        m.metadata?.closure_outcome === 'shut_in'     ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                        m.metadata?.closure_outcome === 'false_alarm' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {m.metadata?.closure_outcome?.replace(/_/g, ' ') ?? '—'}
                      </span>
                      {m.metadata?.closure_sha256 && (
                        <Lock className="w-3.5 h-3.5 text-green-500" />
                      )}
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {/* Full chain-of-custody detail */}
                  {isExpanded && (
                    <div className="px-5 pb-6 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-4">

                      {/* Detection facts */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">AI OGI Findings (Step 6–7)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          {[
                            { label: 'AI Severity',     value: m.metadata?.severity },
                            { label: 'Plume Rate',       value: m.metadata?.plume_rate_kg_hr != null ? `${m.metadata.plume_rate_kg_hr} ${uom.mass_rate_unit_label}` : undefined },
                            { label: 'Thermal Anomaly', value: m.metadata?.thermal_anomaly_c != null ? `+${m.metadata.thermal_anomaly_c}°C` : undefined },
                            { label: 'Safety Decision', value: m.metadata?.step7_status?.replace(/_/g, ' ') },
                          ].filter((r) => r.value).map((r) => (
                            <div key={r.label} className="rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2">
                              <p className="text-gray-400 dark:text-gray-500">{r.label}</p>
                              <p className="font-medium text-gray-800 dark:text-gray-200 capitalize mt-0.5">{r.value}</p>
                            </div>
                          ))}
                        </div>
                        {m.metadata?.diagnosis && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 italic">"{m.metadata.diagnosis}"</p>
                        )}
                      </div>

                      {/* Response facts */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Ground Response (Step 8a)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                          {[
                            { label: 'Response Type', value: RESPONSE_LABELS[m.metadata?.step8_response_type ?? ''] ?? m.metadata?.step8_response_type },
                            { label: 'Team Lead',     value: m.metadata?.step8_team_lead },
                            { label: 'ERP Order',     value: m.metadata?.step8_work_order ? `${m.metadata.step8_erp_system ?? ''} ${m.metadata.step8_work_order}`.trim() : undefined },
                          ].filter((r) => r.value).map((r) => (
                            <div key={r.label} className="rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2">
                              <p className="text-gray-400 dark:text-gray-500">{r.label}</p>
                              <p className="font-medium text-gray-800 dark:text-gray-200 mt-0.5">{r.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Closure facts */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Closure Record (Step 8b)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs mb-2">
                          {[
                            { label: 'Outcome',          value: OUTCOME_LABELS[m.metadata?.closure_outcome ?? ''] },
                            { label: 'Product Loss',     value: m.metadata?.closure_loss_volume_bbl != null ? `${m.metadata.closure_loss_volume_bbl} ${uom.volume_unit_label}` : undefined },
                            { label: 'Repair Method',    value: m.metadata?.closure_repair_method },
                          ].filter((r) => r.value).map((r) => (
                            <div key={r.label} className="rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2">
                              <p className="text-gray-400 dark:text-gray-500">{r.label}</p>
                              <p className="font-medium text-gray-800 dark:text-gray-200 mt-0.5">{r.value}</p>
                            </div>
                          ))}
                        </div>
                        {m.metadata?.closure_notes && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 italic mb-2">"{m.metadata.closure_notes}"</p>
                        )}
                        {m.metadata?.closure_regulatory && (
                          <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400 mb-2">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Regulatory body notified
                          </div>
                        )}
                      </div>

                      {/* SHA-256 integrity seal */}
                      {m.metadata?.closure_sha256 && (
                        <div className="rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 p-3">
                          <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
                            <Lock className="w-3.5 h-3.5" /> SHA-256 Integrity Seal
                          </p>
                          <p className="text-xs font-mono text-green-800 dark:text-green-300 break-all">
                            {m.metadata.closure_sha256}
                          </p>
                          {m.metadata.closure_sealed_at && (
                            <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                              Sealed: {new Date(m.metadata.closure_sealed_at).toLocaleString('en-GB')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Submission guidance ── */}
      <Card padding="sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            <strong className="text-gray-700 dark:text-gray-300">Regulatory submission guidance:</strong> Export the CSV and include the SHA-256 hash column as a tamper-evidence reference.
            For DPR/NUPRC submissions (Nigeria), include the hash alongside the SOPF Form 7 or Form 8 as applicable.
            For EPA submissions, reference the hash in the Spill Prevention, Control, and Countermeasure (SPCC) documentation.
            The hash can be independently verified by recomputing it from the closure record fields.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default RegulatoryReportingPage;

// Made with Bob
